// Demo data for the Governance page

export interface DemoProposal {
  id: string;
  title: string;
  summary: string;
  description: string;
  proposer: string;
  proposerName: string;
  proposerAvatar?: string;
  startBlock: number;
  endBlock: number;
  state: number; // 0: Pending, 1: Active, 2: Canceled, 3: Defeated, 4: Succeeded, 5: Queued, 6: Expired, 7: Executed
  forVotes: string;
  againstVotes: string;
  abstainVotes: string;
  startTime: string;
  endTime: string;
  executionTime?: string;
  transactions: {
    target: string;
    value: string;
    signature: string;
    callData: string;
    description: string;
  }[];
}

export interface TreasuryData {
  totalValueUSD: string;
  tokenBalances: {
    token: string;
    symbol: string;
    balance: string;
    value: string;
    percentage: number;
    logoUrl: string;
  }[];
  recentTransactions: {
    id: string;
    hash: string;
    timestamp: string;
    from: string;
    to: string;
    value: string;
    tokenSymbol: string;
    type: 'income' | 'expense';
    description: string;
  }[];
}

// Demo proposals
export const demoProposals: DemoProposal[] = [
  {
    id: "1",
    title: "FTTF-1: Platform Fee Adjustment",
    summary: "Proposal to adjust platform fees from 2.5% to 2% to increase competitiveness",
    description: "This proposal aims to reduce the platform fee from the current 2.5% to 2% on all transactions. The goal is to make FTTF.World more competitive in the market and attract more creators and users. The fee reduction is expected to be offset by increased transaction volume. The treasury will monitor the impact on revenue for 3 months after implementation.",
    proposer: "0x1234567890abcdef1234567890abcdef12345678",
    proposerName: "Alex Rivers",
    proposerAvatar: "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1180&q=80",
    startBlock: 15000000,
    endBlock: 15100000,
    state: 4, // Succeeded
    forVotes: "1250000",
    againstVotes: "320000",
    abstainVotes: "50000",
    startTime: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(), // 15 days ago
    endTime: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000).toISOString(), // 8 days ago
    executionTime: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days ago
    transactions: [
      {
        target: "0xFeeController",
        value: "0",
        signature: "setFeePercentage(uint256)",
        callData: "0x00000000000000000000000000000000000000000000000000000000000001f4", // 500 basis points (2%)
        description: "Set platform fee to 2%"
      }
    ]
  },
  {
    id: "2",
    title: "FTTF-2: Creator Grants Program",
    summary: "Establish a 100,000 FTTF token grant program for emerging creators",
    description: "This proposal seeks to establish a Creator Grants Program with an initial allocation of 100,000 FTTF tokens from the treasury. The program will provide grants ranging from 1,000 to 5,000 FTTF tokens to emerging creators who demonstrate potential and commitment to the platform. A grants committee consisting of 5 community members will review applications monthly. The program aims to attract and retain talented creators, especially in underrepresented content categories.",
    proposer: "0x2345678901abcdef2345678901abcdef23456789",
    proposerName: "Maya Johnson",
    proposerAvatar: "https://images.unsplash.com/photo-1570295999919-56ceb5ecca61?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1180&q=80",
    startBlock: 15200000,
    endBlock: 15300000,
    state: 1, // Active
    forVotes: "980000",
    againstVotes: "120000",
    abstainVotes: "30000",
    startTime: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(), // 3 days ago
    endTime: new Date(Date.now() + 4 * 24 * 60 * 60 * 1000).toISOString(), // 4 days from now
    transactions: [
      {
        target: "0xTreasury",
        value: "0",
        signature: "allocateFunds(address,uint256)",
        callData: "0x000000000000000000000000GrantsProgramMultisig000000000000000000000005F5E100", // 100,000 tokens
        description: "Allocate 100,000 FTTF tokens to Grants Program multisig"
      }
    ]
  },
  {
    id: "3",
    title: "FTTF-3: Protocol Upgrade",
    summary: "Upgrade to the latest Livepeer protocol version for improved performance",
    description: "This proposal recommends upgrading our integration with the Livepeer protocol to the latest version. The upgrade will bring several benefits including improved transcoding performance, lower latency for live streams, and better handling of network congestion. The technical team has tested the upgrade in a staging environment for two weeks with positive results. The upgrade requires a 4-hour maintenance window which will be scheduled during low-usage hours.",
    proposer: "0x3456789012abcdef3456789012abcdef34567890",
    proposerName: "Ethan Wright",
    proposerAvatar: "https://images.unsplash.com/photo-1633332755192-727a05c4013d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1180&q=80",
    startBlock: 15400000,
    endBlock: 15500000,
    state: 7, // Executed
    forVotes: "1750000",
    againstVotes: "50000",
    abstainVotes: "25000",
    startTime: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days ago
    endTime: new Date(Date.now() - 23 * 24 * 60 * 60 * 1000).toISOString(), // 23 days ago
    executionTime: new Date(Date.now() - 21 * 24 * 60 * 60 * 1000).toISOString(), // 21 days ago
    transactions: [
      {
        target: "0xProtocolController",
        value: "0",
        signature: "upgradeProtocol(string)",
        callData: "0x76312e352e30", // "v1.5.0" in hex
        description: "Upgrade Livepeer protocol to v1.5.0"
      }
    ]
  },
  {
    id: "4",
    title: "FTTF-4: Community Events Budget",
    summary: "Allocate 50,000 FTTF for quarterly community events and hackathons",
    description: "This proposal requests an allocation of 50,000 FTTF tokens to fund quarterly community events and hackathons over the next 12 months. The events will include virtual meetups, creator workshops, and two major hackathons with prizes. These activities aim to strengthen community engagement, attract developers to build on our platform, and increase awareness of FTTF.World in the broader web3 ecosystem. A detailed budget breakdown is included in the full proposal.",
    proposer: "0x4567890123abcdef4567890123abcdef45678901",
    proposerName: "Sophia Chen",
    proposerAvatar: "https://images.unsplash.com/photo-1527980965255-d3b416303d12?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1180&q=80",
    startBlock: 15600000,
    endBlock: 15700000,
    state: 3, // Defeated
    forVotes: "450000",
    againstVotes: "780000",
    abstainVotes: "120000",
    startTime: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString(), // 45 days ago
    endTime: new Date(Date.now() - 38 * 24 * 60 * 60 * 1000).toISOString(), // 38 days ago
    transactions: [
      {
        target: "0xTreasury",
        value: "0",
        signature: "allocateFunds(address,uint256)",
        callData: "0x000000000000000000000000EventsMultisig000000000000000000000002FAF080", // 50,000 tokens
        description: "Allocate 50,000 FTTF tokens to Events multisig"
      }
    ]
  },
  {
    id: "5",
    title: "FTTF-5: Treasury Diversification",
    summary: "Diversify 15% of treasury holdings into stablecoins for operational stability",
    description: "This proposal suggests diversifying 15% of the treasury's FTTF token holdings into stablecoins (USDC and DUSD) to ensure operational stability and reduce volatility risk. The diversification will be executed gradually over 3 months to minimize market impact. The stablecoin reserves will be used for ongoing operational expenses, creator grants, and as a buffer during market downturns. The treasury committee will provide monthly reports on the execution progress and impact.",
    proposer: "0x5678901234abcdef5678901234abcdef56789012",
    proposerName: "James Wilson",
    proposerAvatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80",
    startBlock: 15800000,
    endBlock: 15900000,
    state: 1, // Active
    forVotes: "890000",
    againstVotes: "650000",
    abstainVotes: "75000",
    startTime: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(), // 2 days ago
    endTime: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString(), // 5 days from now
    transactions: [
      {
        target: "0xTreasury",
        value: "0",
        signature: "approveSwap(address,uint256)",
        callData: "0x000000000000000000000000SwapRouter000000000000000000000000000000000000000000000000", // Approve swap
        description: "Approve treasury diversification strategy"
      }
    ]
  }
];

// Demo treasury data
export const demoTreasuryData: TreasuryData = {
  totalValueUSD: "$4,250,000",
  tokenBalances: [
    {
      token: "FTTF",
      symbol: "FTTF",
      balance: "3,500,000",
      value: "3,500,000",
      percentage: 82,
      logoUrl: "https://cryptologos.cc/logos/ethereum-eth-logo.svg?v=024"
    },
    {
      token: "DUSD",
      symbol: "DUSD",
      balance: "450,000",
      value: "450,000",
      percentage: 11,
      logoUrl: "https://cryptologos.cc/logos/multi-collateral-dai-dai-logo.svg?v=024"
    },
    {
      token: "USDC",
      symbol: "USDC",
      balance: "300,000",
      value: "300,000",
      percentage: 7,
      logoUrl: "https://cryptologos.cc/logos/usd-coin-usdc-logo.svg?v=024"
    }
  ],
  recentTransactions: [
    {
      id: "tx1",
      hash: "0x1234567890abcdef1234567890abcdef1234567890abcdef1234567890abcdef",
      timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      from: "0xTreasury",
      to: "0xGrantsProgramMultisig",
      value: "25,000",
      tokenSymbol: "FTTF",
      type: "expense",
      description: "Creator Grants Program - Q2 Allocation"
    },
    {
      id: "tx2",
      hash: "0x2345678901abcdef2345678901abcdef2345678901abcdef2345678901abcdef",
      timestamp: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
      from: "0xFeeCollector",
      to: "0xTreasury",
      value: "12,500",
      tokenSymbol: "FTTF",
      type: "income",
      description: "Platform Fee Revenue - Week 22"
    },
    {
      id: "tx3",
      hash: "0x3456789012abcdef3456789012abcdef3456789012abcdef3456789012abcdef",
      timestamp: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
      from: "0xTreasury",
      to: "0xDevelopmentFund",
      value: "30,000",
      tokenSymbol: "FTTF",
      type: "expense",
      description: "Development Team - Monthly Allocation"
    },
    {
      id: "tx4",
      hash: "0x4567890123abcdef4567890123abcdef4567890123abcdef4567890123abcdef",
      timestamp: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
      from: "0xSwapRouter",
      to: "0xTreasury",
      value: "50,000",
      tokenSymbol: "DUSD",
      type: "income",
      description: "Treasury Diversification - FTTF to DUSD"
    },
    {
      id: "tx5",
      hash: "0x5678901234abcdef5678901234abcdef5678901234abcdef5678901234abcdef",
      timestamp: new Date(Date.now() - 12 * 24 * 60 * 60 * 1000).toISOString(),
      from: "0xTreasury",
      to: "0xMarketingFund",
      value: "15,000",
      tokenSymbol: "FTTF",
      type: "expense",
      description: "Marketing Campaign - Creator Spotlight"
    },
    {
      id: "tx6",
      hash: "0x6789012345abcdef6789012345abcdef6789012345abcdef6789012345abcdef",
      timestamp: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
      from: "0xFeeCollector",
      to: "0xTreasury",
      value: "9,800",
      tokenSymbol: "FTTF",
      type: "income",
      description: "Platform Fee Revenue - Week 21"
    }
  ]
};

// Governance stats
export const governanceStats = {
  totalProposals: 5,
  activeProposals: 2,
  totalVotes: 7500000,
  voterParticipation: 32, // percentage
  quorum: 20, // percentage
  proposalThreshold: 100000, // minimum tokens to create proposal
};

// Helper function to get proposal state name
export const getProposalStateName = (state: number): string => {
  const states = [
    "Pending",
    "Active",
    "Canceled",
    "Defeated",
    "Succeeded",
    "Queued",
    "Expired",
    "Executed"
  ];
  return states[state] || "Unknown";
};

// Helper function to get proposal state color
export const getProposalStateColor = (state: number): string => {
  switch (state) {
    case 1: // Active
      return "primary";
    case 3: // Defeated
      return "red";
    case 4: // Succeeded
    case 7: // Executed
      return "green";
    default:
      return "gray";
  }
};